// Pure formatters for Overview-page metrics. Pure so they can be unit-tested
// without rendering, and reused by future surfaces (Members table, alert
// dialogs) without dragging React into the dependency graph.

import type { CommunityHealthColor } from "@community-analytics/shared";

/**
 * Compact display for member counts and message totals: 1_234 → "1.2K",
 * 1_234_567 → "1.2M". Keeps cards readable on a 320-wide phone viewport.
 *
 * Negative values (rare — used only for net-growth deltas) are formatted
 * with a leading minus and the same magnitude rules.
 */
export function formatCompactNumber(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  if (abs < 1_000) return `${sign}${abs}`;
  if (abs < 1_000_000) return `${sign}${trim(abs / 1_000)}K`;
  if (abs < 1_000_000_000) return `${sign}${trim(abs / 1_000_000)}M`;
  return `${sign}${trim(abs / 1_000_000_000)}B`;
}

function trim(n: number): string {
  // 1.0 → "1", 1.2 → "1.2", 12.34 → "12". Never more than one decimal.
  const fixed = n.toFixed(1);
  return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
}

/**
 * Signed delta for the "net growth (24h)" card. Renders "+12", "-5", "0"
 * (zero is neither positive nor negative on the wire so we suppress the
 * sign). Never returns "+0" or "-0".
 */
export function formatSignedDelta(value: number): string {
  if (!Number.isFinite(value) || value === 0) return "0";
  return value > 0
    ? `+${formatCompactNumber(value)}`
    : formatCompactNumber(value);
}

/**
 * Map a §6.3.5 health color enum into a Tailwind CSS variable name. The
 * gauge component reads this to color the arc; a future theme swap only
 * has to update CSS variables, not chase enum strings around the codebase.
 */
export function healthColorClass(color: CommunityHealthColor): string {
  switch (color) {
    case "green":
      return "text-emerald-500";
    case "blue":
      return "text-sky-500";
    case "yellow":
      return "text-amber-500";
    case "red":
      return "text-rose-500";
  }
}

/**
 * Same mapping but for fill (used by the gauge's stroke). Keeping the
 * fill helper distinct from the text helper means a future redesign can
 * differentiate text vs. arc colors without touching every caller.
 */
export function healthStrokeColor(color: CommunityHealthColor): string {
  switch (color) {
    case "green":
      return "rgb(16 185 129)"; // emerald-500
    case "blue":
      return "rgb(14 165 233)"; // sky-500
    case "yellow":
      return "rgb(245 158 11)"; // amber-500
    case "red":
      return "rgb(244 63 94)"; // rose-500
  }
}
